// Ship to home: the delivery address for a completed bill of this store, sent
// with markFashionShipToHome. The bill then shows in Online orders as channel
// "In store", where it is packed and shipped. Online only.
// The form only checks the fields itself; the server's answer goes to the host
// through onDone / onError ({ ok, message, id }) so the host shows it in its
// one toast. Validation problems stay next to the field.
import React, { useRef, useState } from "react";
import { markFashionShipToHome } from "../../../../services";
import { shipToDraft, validateShipTo } from "../../../../utils/fashion/shipTo";
import "./shipToHome.css";

const FIELDS = [
  { key: "name", label: "Name", auto: "name", required: true },
  { key: "mobileNo", label: "Mobile", auto: "tel", required: true, mode: "tel" },
  { key: "line1", label: "Address", auto: "address-line1", required: true, wide: true },
  { key: "line2", label: "Address line 2", auto: "address-line2", wide: true },
  { key: "landmark", label: "Landmark", auto: "off" },
  { key: "city", label: "City", auto: "address-level2", required: true },
  { key: "state", label: "State", auto: "address-level1" },
  { key: "pincode", label: "PIN code", auto: "postal-code", required: true, mode: "numeric" },
];

export default function ShipToHomeForm({ orderId, documentNo, bunitId, customer, isOnline, onDone, onError, onCancel }) {
  const [draft, setDraft] = useState(() => shipToDraft(customer));
  const [err, setErr] = useState(null); // { field, error }
  const [busy, setBusy] = useState(false);
  const inputs = useRef({});
  const offline = isOnline === false;

  const set = (key) => (e) => {
    const v = e.target.value;
    setDraft((d) => ({ ...d, [key]: v }));
    if (err?.field === key) setErr(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (offline) {
      setErr({ field: null, error: "Shipping needs a connection. Reconnect the till and try again." });
      return;
    }
    if (!orderId || !bunitId) {
      setErr({ field: null, error: "This bill can't be shipped from here." });
      return;
    }
    const res = validateShipTo(draft);
    if (res.error) {
      setErr(res);
      inputs.current[res.field]?.focus();
      return;
    }
    setErr(null);
    setBusy(true);
    let out;
    try {
      out = await markFashionShipToHome(orderId, bunitId, res.address);
    } catch {
      out = { ok: false, message: "Couldn't mark the bill to ship. Try again." };
    }
    setBusy(false);
    if (out?.ok) onDone?.({ ...out, address: res.address });
    else onError?.(out || { ok: false, message: "Couldn't mark the bill to ship. Try again." });
  };

  return (
    <form className="fp-sth" onSubmit={submit} noValidate aria-label={`Ship ${documentNo || "bill"} to home`}>
      <p className="fp-sth-note">The parcel goes by courier. Once saved, pack and ship it from Online orders. It can't be cancelled there; return it from the bill instead.</p>
      <div className="fp-sth-grid">
        {FIELDS.map((f) => {
          const bad = err?.field === f.key;
          const id = `fp-sth-${f.key}`;
          return (
            <label key={f.key} className={`fp-sth-field${f.wide ? " is-wide" : ""}`} htmlFor={id}>
              <span className="fp-sth-lbl">
                {f.label}
                {!f.required && <small> (optional)</small>}
              </span>
              <input
                id={id}
                ref={(el) => {
                  inputs.current[f.key] = el;
                }}
                className={`fp-sth-input${bad ? " is-bad" : ""}`}
                value={draft[f.key]}
                onChange={set(f.key)}
                autoComplete={f.auto}
                inputMode={f.mode}
                aria-invalid={bad ? "true" : "false"}
                aria-describedby={bad ? "fp-sth-err" : undefined}
                disabled={busy}
              />
            </label>
          );
        })}
      </div>
      {err && (
        <p id="fp-sth-err" className="fp-sth-err" role="alert">
          {err.error}
        </p>
      )}
      <div className="fp-sth-acts">
        {onCancel && (
          <button type="button" className="fp-sth-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        )}
        <button type="submit" className="fp-sth-btn is-pri" disabled={busy || offline}>
          {busy ? "Saving…" : "Ship to home"}
        </button>
      </div>
    </form>
  );
}
